/* KCIS University LMS shared styling helpers */

import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, GraduationCap } from "lucide-react";

export function UniHeader({ title, back }) {
  return (
    <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {back ? (
            <Link to={back} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm" data-testid="uni-back-link">
              <ArrowLeft className="w-4 h-4" />Back
            </Link>
          ) : null}
          <GraduationCap className="w-5 h-5 text-purple-900" />
          <span className="font-display text-slate-900">{title}</span>
        </div>
        <Link to="/university/student" className="text-xs text-purple-900 hover:underline">My Portal →</Link>
      </div>
    </header>
  );
}

/** Render lesson markdown with Vimeo / Loom shortcodes.
 * - `[vimeo:VIDEO_ID]`  → 16:9 Vimeo iframe
 * - `[loom:VIDEO_ID]`   → 16:9 Loom embed
 * Otherwise renders as plain pre-wrapped text (markdown rendering kept
 * minimal to avoid a heavy dependency — headings (# / ##) & bullets
 * (- / *) are paragraph-broken so the result is readable.)
 */
export function LessonContent({ content }) {
  if (!content) return <p className="text-sm text-slate-500">No content yet.</p>;
  const blocks = [];
  const lines = content.split(/\r?\n/);
  let para = [];
  const flush = () => {
    if (para.length) {
      blocks.push({ kind: "p", text: para.join("\n") });
      para = [];
    }
  };
  for (const raw of lines) {
    const line = raw;
    const vimeo = line.match(/^\[vimeo:([A-Za-z0-9-]+)\]\s*$/i);
    const loom = line.match(/^\[loom:([A-Za-z0-9-]+)\]\s*$/i);
    if (vimeo) { flush(); blocks.push({ kind: "vimeo", id: vimeo[1] }); continue; }
    if (loom)  { flush(); blocks.push({ kind: "loom",  id: loom[1] }); continue; }
    if (/^#{1,3}\s+/.test(line)) {
      flush();
      const level = (line.match(/^#+/) || [""])[0].length;
      blocks.push({ kind: "h", level, text: line.replace(/^#+\s+/, "") });
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flush();
      blocks.push({ kind: "li", text: line.replace(/^[-*]\s+/, "") });
      continue;
    }
    if (line.trim() === "") {
      flush();
      continue;
    }
    para.push(line);
  }
  flush();
  return (
    <div className="prose prose-slate max-w-none text-[15px] leading-relaxed">
      {blocks.map((b, i) => {
        if (b.kind === "h") {
          const Tag = `h${Math.min(3, b.level)}`;
          return <Tag key={i} className="font-display text-slate-900 mt-6 mb-2">{b.text}</Tag>;
        }
        if (b.kind === "li") {
          return <p key={i} className="mb-1.5 pl-4 relative"><span className="absolute left-0 top-2 w-1.5 h-1.5 bg-purple-900 rounded-full" />{b.text}</p>;
        }
        if (b.kind === "vimeo") {
          return (
            <div key={i} className="my-4 aspect-video rounded-xl overflow-hidden border border-slate-200">
              <iframe
                title="Vimeo lesson video"
                src={`https://player.vimeo.com/video/${b.id}`}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        if (b.kind === "loom") {
          return (
            <div key={i} className="my-4 aspect-video rounded-xl overflow-hidden border border-slate-200">
              <iframe
                title="Loom lesson video"
                src={`https://www.loom.com/embed/${b.id}`}
                className="w-full h-full"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
          );
        }
        return <p key={i} className="mb-3 whitespace-pre-wrap">{b.text}</p>;
      })}
    </div>
  );
}

export const usd = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const fmtDateRange = (start, end) => {
  if (!start) return "—";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const dOpt = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
  const tOpt = { hour: "numeric", minute: "2-digit" };
  if (!e || s.toDateString() === e.toDateString()) {
    return `${s.toLocaleDateString(undefined, dOpt)} · ${s.toLocaleTimeString(undefined, tOpt)}${e ? " – " + e.toLocaleTimeString(undefined, tOpt) : ""}`;
  }
  return `${s.toLocaleDateString(undefined, dOpt)} – ${e.toLocaleDateString(undefined, dOpt)}`;
};

/**
 * Format an event's schedule, honoring multi-day `sessions` when present.
 *
 *   1-day event:   "Wed, Aug 10, 2026 · 10:00 AM – 4:00 PM"
 *   2-day event w/ identical times each day:
 *                  "Aug 10–11, 2026 · 10:00 AM – 4:00 PM each day"
 *   2-day event w/ different times:
 *                  "Aug 10, 2026 10:00 AM – 4:00 PM · Aug 11, 2026 9:00 AM – 3:00 PM"
 */
export const fmtSessionRange = (event) => {
  const sessions = (event && event.sessions) || [];
  if (sessions.length === 0) return fmtDateRange(event?.starts_at, event?.ends_at);

  const dOptShort = { month: "short", day: "numeric" };
  const dOptLong  = { month: "short", day: "numeric", year: "numeric" };
  const tOpt = { hour: "numeric", minute: "2-digit" };

  // Convert each session to its display parts
  const parts = sessions
    .filter((s) => s.starts_at)
    .map((s) => {
      const sd = new Date(s.starts_at);
      const ed = s.ends_at ? new Date(s.ends_at) : null;
      return { sd, ed };
    });
  if (parts.length === 0) return "—";

  // Check if every session has the same start-of-day-time and same end-of-day-time
  const allSameTime = parts.every((p, _i, arr) =>
    arr[0].sd.getHours() === p.sd.getHours() &&
    arr[0].sd.getMinutes() === p.sd.getMinutes() &&
    (arr[0].ed && p.ed
      ? arr[0].ed.getHours() === p.ed.getHours() &&
        arr[0].ed.getMinutes() === p.ed.getMinutes()
      : !arr[0].ed && !p.ed)
  );

  const first = parts[0];
  const last  = parts[parts.length - 1];
  const year  = last.sd.getFullYear();

  if (parts.length >= 2 && allSameTime) {
    // "Aug 10–11, 2026 · 10:00 AM – 4:00 PM each day"
    const sameMonth = first.sd.getMonth() === last.sd.getMonth();
    const head = sameMonth
      ? `${first.sd.toLocaleDateString(undefined, dOptShort)}–${last.sd.getDate()}, ${year}`
      : `${first.sd.toLocaleDateString(undefined, dOptShort)} – ${last.sd.toLocaleDateString(undefined, dOptShort)}, ${year}`;
    const t = `${first.sd.toLocaleTimeString(undefined, tOpt)}${first.ed ? " – " + first.ed.toLocaleTimeString(undefined, tOpt) : ""}`;
    return `${head} · ${t} each day`;
  }

  if (parts.length === 1) {
    return fmtDateRange(parts[0].sd.toISOString(), parts[0].ed ? parts[0].ed.toISOString() : "");
  }

  // Heterogeneous sessions — list each
  return parts
    .map(({ sd, ed }) =>
      `${sd.toLocaleDateString(undefined, dOptLong)} ${sd.toLocaleTimeString(undefined, tOpt)}${ed ? " – " + ed.toLocaleTimeString(undefined, tOpt) : ""}`
    )
    .join(" · ");
};
